// components/NumberTicker.tsx
import React, { useEffect, useRef, useState } from "react";

interface NumberTickerProps {
  value: number;
  className?: string;
  /**
   * Animation duration in ms (clamped between 80 and 5000).
   * Larger durations = slower interpolation.
   */
  durationMs?: number;
  /**
   * Optional custom formatter (defaults to Intl.NumberFormat with no decimals).
   */
  format?: (n: number) => string;
  /**
   * Custom easing (maps t in [0,1] -> eased t). Default: easeOutCubic.
   */
  easing?: (t: number) => number;
}

const defaultFormat = (n: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const NumberTicker: React.FC<NumberTickerProps> = ({
  value,
  className,
  durationMs = 700,
  format = defaultFormat,
  easing = easeOutCubic,
}) => {
  const [displayed, setDisplayed] = useState<number>(value);

  // Refs to manage RAF animation
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const startValueRef = useRef<number>(value);
  const targetRef = useRef<number>(value);
  const lastEasedRef = useRef<number>(1);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  // Start / restart animation whenever "value" changes
  useEffect(() => {
    // cancel any existing RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Start from the currently-displayed number (so mid-animation jumps are smooth)
    startValueRef.current = displayed;
    targetRef.current = value;
    startTimeRef.current = null;

    // clamp duration to reasonable bounds
    const duration = Math.max(80, Math.min(durationMs, 5000));

    const step = (now: number) => {
      if (!startTimeRef.current) startTimeRef.current = now;
      const elapsed = now - startTimeRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easing(t);
      lastEasedRef.current = eased;

      const current =
        startValueRef.current +
        (targetRef.current - startValueRef.current) * eased;

      setDisplayed(Math.round(current));

      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        // ensure final value is exact
        setDisplayed(targetRef.current);
        lastEasedRef.current = 1;
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(step);

    // cleanup for this effect if value changes again
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // `displayed` is intentionally not in deps; we want the current rendered displayed used at the time value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs, easing]);

  // Subtle scale effect while animating (reads lastEasedRef which is updated each frame).
  // Because setDisplayed runs each frame, component re-renders frequently and will pick up the latest eased.
  const scale = 1 + 0.06 * (1 - (lastEasedRef.current ?? 1));
  const formatted = format(displayed);

  return (
    <div
      className={className}
      aria-live="polite"
      style={{
        transform: `scale(${scale})`,
        transition: "transform 120ms linear",
        willChange: "transform",
      }}
    >
      {formatted}
    </div>
  );
};

export default NumberTicker;
